import { releaseDecisionGraph } from "@imminently/decisively-core";
import {
  Background,
  BackgroundVariant,
  ConnectionLineType,
  type Edge,
  type Node,
  ReactFlow,
  ReactFlowProvider,
  applyNodeChanges,
} from "@xyflow/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { PostSolveResponse } from "../../pages/internal-debugger/InternalDebuggerPage";
import InternalDebuggerRuleNode from "./InternalDebuggerRuleNode";
import InternalDebuggerZoomPanControls from "./InternalDebuggerZoomPanControls";
import "./InternalDebuggerNodeGraph.scss";
import "@xyflow/react/dist/style.css";
import { GraphNode, type Release } from "@packages/commons";

const worker = typeof Worker !== "undefined" ? new Worker("/internalDagreWorker.js") : undefined;

export const NodeGraphSolveContext = React.createContext<PostSolveResponse | undefined>(undefined);

export interface DebuggerNodeGraphProps {
  release?: Release;
  solveResult?: PostSolveResponse | null;
  selectedNodeContext: {
    selected: string | undefined;
    setSelected: (id: string | undefined) => void;
  };
}

interface Elements {
  nodes: Node[];
  edges: Edge[];
}

const NODE_TYPES = {
  rule: InternalDebuggerRuleNode,
};

const InternalDebuggerNodeGraph = (props: DebuggerNodeGraphProps) => {
  const { release } = props;
  const [elements, setElements] = useState<Elements>({
    nodes: [],
    edges: [],
  });
  const [fitViewKey, setFitViewKey] = useState(0);
  const [layouting, setLayouting] = useState(false);

  useEffect(() => {
    if (release) {
      setLayouting(true);
      worker!.postMessage({
        rawGraph: releaseDecisionGraph(release)!.toBytes(),
      });
    }
  }, [release?.id]);

  const [searchValue, setSearchValue] = useState("");

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  }, []);

  const handleSearchReset = useCallback(() => {
    setSearchValue("");
  }, []);

  const searchNodes = useMemo(() => {
    if (searchValue.length > 0) {
      const results = elements.nodes.filter(
        (element) => element.data && element.id.toLowerCase().startsWith(searchValue.toLowerCase()),
      );
      if (results.length > 0) return results;
      else
        return elements.nodes.filter((element) =>
          element.data?.description
            // @ts-ignore
            ?.toLowerCase()
            .includes(searchValue.toLowerCase()),
        );
    }
    return elements.nodes;
  }, [searchValue, elements.nodes]);

  let panTo: Node | undefined = undefined;
  if (searchNodes.length > 0) {
    panTo = searchNodes[0] as Node;
  }

  // web worker
  const workerListener = React.useCallback((e: MessageEvent<any>) => {
    const elements = e.data;

    setElements(elements);
    setFitViewKey((i) => i + 1);
    setLayouting(false);
  }, []);

  React.useEffect(() => {
    worker!.addEventListener("message", workerListener);

    return () => {
      worker!.removeEventListener("message", workerListener);
    };
  }, [workerListener]);

  return (
    <div className="NodeGraph">
      <ReactFlowProvider>
        <InternalDebuggerZoomPanControls
          panTo={panTo}
          fitViewKey={fitViewKey}
        />
        <ReactFlow
          nodeTypes={NODE_TYPES}
          nodes={elements.nodes}
          edges={elements.edges}
          onNodesChange={(changes) =>
            setElements((elements) => ({
              nodes: applyNodeChanges(changes, elements.nodes),
              edges: elements.edges,
            }))
          }
          connectionLineType={ConnectionLineType.SmoothStep}
          minZoom={0}
          nodesConnectable={false}
        >
          <Background
            variant={BackgroundVariant.Lines}
            gap={64}
            size={1}
            color={"#eee"}
          />
        </ReactFlow>
        <div className={"NodeGraph__controls"}>
          <div>
            <label htmlFor={"node-search"}>Search </label>
            <input
              value={searchValue}
              onChange={handleSearchChange}
              id={"node-search"}
            />
            <button onClick={handleSearchReset}>Reset</button>
          </div>
          <small>found {searchNodes.length}</small>
        </div>
        {layouting && (
          <div className="NodeGraph__loading">
            <div
              aria-busy="true"
              aria-describedby="progress-bar"
            >
              Laying out graph
            </div>
            <small>this could take a while</small>
            <progress
              id="progress-bar"
              aria-label="Content loading…"
            ></progress>
          </div>
        )}
      </ReactFlowProvider>
    </div>
  );
};

export default InternalDebuggerNodeGraph;
